const GRID_PARTS_MIN = 1;
const GRID_PARTS_MAX = 100;
// At or below this piece count, a single row (or single column) — e.g. 7 pieces
// side by side — reads as a normal, intentional layout. Above it, the same shape
// reads as broken rather than intentional (see resolveGridPartCount below).
const SINGLE_ROW_MAX = 10;
// Max acceptable |ln(cellAspect)| above SINGLE_ROW_MAX pieces — roughly, no cell
// side may be more than ~2x the other. A perfect square piece count (9, 16, 25,
// 36, 64, 100 — every preset) scores exactly ln(imageAspectRatio) ≈ 0.575 at the
// default 16:9 image ratio, so this must clear that or every preset would get
// needlessly nudged too; it's picked to admit those while still rejecting thin
// slivers like 82 = 2×41 (score ≈ 2.45).
const GRID_SCORE_MAX = 0.7;
// Only a fallback for when the real image shape is unknown: puzzle images can be any size, and
// callers pass the uploaded image's own aspect ratio.
const DEFAULT_ASPECT_RATIO = 1920 / 1080;

/**
 * Best rows×cols for exactly n (any factor pair of n, including a 1×n or n×1
 * sliver when n has no better-shaped factorization, e.g. n is prime) at the
 * given aspect ratio. Always returns a result — every n >= 1 factors at least
 * trivially as 1×n.
 */
function bestGridForCount(n, ar) {
  let bestRows = 1;
  let bestCols = n;
  let bestScore = Infinity;

  for (let rows = 1; rows <= n; rows += 1) {
    if (n % rows !== 0) continue;
    const cols = n / rows;
    const cellAspect = ar * (rows / cols);
    const score = Math.abs(Math.log(cellAspect));
    if (score < bestScore) {
      bestScore = score;
      bestRows = rows;
      bestCols = cols;
    }
  }

  return { rows: bestRows, cols: bestCols, score: bestScore };
}

function isGoodShape(n, ar) {
  return bestGridForCount(n, ar).score <= GRID_SCORE_MAX;
}

/** Nearest count to `requested` (either direction) with a reasonably square best shape. */
function nearestGoodShapeCount(requested, ar) {
  for (let delta = 1; delta <= GRID_PARTS_MAX; delta += 1) {
    const up = requested + delta;
    const down = requested - delta;
    if (up <= GRID_PARTS_MAX && isGoodShape(up, ar)) return up;
    if (down >= GRID_PARTS_MIN && isGoodShape(down, ar)) return down;
  }
  return requested;
}

/**
 * The requested piece count is honored exactly for everything up to
 * SINGLE_ROW_MAX, and for any count above it whose best shape is already
 * reasonably square (the vast majority — only primes and near-primes like
 * 82 = 2×41 aren't). Above SINGLE_ROW_MAX, a count whose only good-ish shape is
 * a thin sliver (a single row/column, or something close to it, e.g. 11, 13,
 * 82) is nudged to the nearest count that isn't, since a strip of 11+ thin
 * tiles reads as broken rather than an intentional layout the way a strip of
 * 7 or 10 does.
 */
export function resolveGridPartCount(requested, imageAspectRatio = DEFAULT_ASPECT_RATIO) {
  const ar = Number(imageAspectRatio) > 0 ? Number(imageAspectRatio) : DEFAULT_ASPECT_RATIO;
  if (requested <= SINGLE_ROW_MAX) return requested;
  if (isGoodShape(requested, ar)) return requested;
  return nearestGoodShapeCount(requested, ar);
}

/**
 * Choose rows × cols = parts so each grid cell is as close to square as
 * possible for the given aspect ratio. `parts` matches the requested count
 * exactly except in the single-row-above-10 case described above.
 */
export function computePuzzleGrid(parts, imageAspectRatio = DEFAULT_ASPECT_RATIO) {
  const requested = Math.max(GRID_PARTS_MIN, Math.min(GRID_PARTS_MAX, Math.floor(Number(parts)) || 9));
  const ar = Number(imageAspectRatio) > 0 ? Number(imageAspectRatio) : DEFAULT_ASPECT_RATIO;
  const n = resolveGridPartCount(requested, ar);
  const grid = bestGridForCount(n, ar);

  return { parts: n, rows: grid.rows, cols: grid.cols };
}

/** True when grid cells are (nearly) square for the given image aspect ratio. */
export function areCellsSquare(rows, cols, imageAspectRatio) {
  const ar = Number(imageAspectRatio) > 0 ? Number(imageAspectRatio) : 4 / 3;
  const cellAspect = ar * (rows / cols);
  return Math.abs(cellAspect - 1) < 0.15;
}
